import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { createLead } from "../../../services/admin.services";
import { useDispatch, useSelector } from "react-redux";
import {
  setChildLeads,
  setLeads,
  setMode,
  setParentLead,
  setSelectedLead,
} from "../../../state/reducers/leadSlice";
import Swal from "sweetalert2";
import { sendSwotTest, updateLeadForm } from "../../../services/lead.services";
import Select from "react-select";
import CreateLeadAbroadForm from "./CreateLeadAbroadForm";
import CreatableSelect from "react-select/creatable";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import CreateLeadCareerCounsellingForm from "./CreateLeadCareerCounsellingForm";
import CreateLeadTestPrepForm from "./CreateLeadTestPrepForm";
import CreateLeadVisaForm from "./CreateLeadVisaForm";
import CreateLeadProfileBuildingForm from "./CreateLeadProfileBuildingForm";
import CreateLeadOthersForm from "./CreateLeadOthersForm";
import Modal from "react-modal";
import { toast } from "react-hot-toast";
import { getNewTable } from "../../../services/mainServices.services";
import AssignToAdminModal from "./AssignToAdminModal";
import CopyLinkModal from "./CopyLinkModal";
import { formatDateTime } from "../../../utils/dateFormat";

function CreateLeadForm({ toggleModal, fetchLeads }) {
  const dispatch = useDispatch();
  const mode = useSelector((state) => state.leads.mode);
  const userDetails = useSelector((state) => state.user.userDetails);
  const admins = useSelector((state) => state.admin.admins);
  const selectedLead = useSelector((state) => state.leads.selectedLead);
  const parentLead = useSelector((state) => state.leads.parentLead);
  const childLeads = useSelector((state) => state.leads.childLeads);
  const [formData, setFormData] = useState({});
  const [assignAdminModal, setAssignAdminModal] = useState(false);
  const [coreServices, setCoreServices] = useState([]);
  const [activeSection, setActiveSection] = useState("leadInfo");
  const [customerId, setCustomerId] = useState();
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const allLeads = useSelector((state) => state.leads.leads);
  const [isCopyEnrollForm, setIsCopyEnrollForm] = useState(false);
  const link = `${process.env.REACT_APP_CURRENT_URL}/lead-form/${selectedLead?.encrypted_id}`;
  const enrolllink = `${process.env.REACT_APP_CURRENT_URL}/enroll/${selectedLead?.encrypted_id}`;
  const sectionsRef = {
    leadInfo: useRef(null),
    referralSource: useRef(null),
    educationCareer: useRef(null),
    attributesHobbies: useRef(null),
    careerIntrests: useRef(null),
    technologyInnovation: useRef(null),
    examTestPrep: useRef(null),
    parentInformation: useRef(null),
    selfDescription: useRef(null),
    visa: useRef(null),
  };

  // Helper function to transform tags to options for Select components
  const transformTagsToOptions = (tags) => {
    if (tags) {
      return tags.map((tag) => ({ label: tag, value: tag }));
    }
    return [];
  };
  // Get core services from the API
  const getAllCoreService = async () => {
    try {
      setIsLoading(true);
      const res = await getNewTable();
      setCoreServices(res || []);
    } catch (err) {
      console.error("Error fetching core services:", err);
      toast.error("Failed to load core services");
      setCoreServices([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Transform options back to tags
  const transformOptionsToTags = (options) => {
    return options.map((option) => option.value);
  };

  // Handle section navigation
  const handleScroll = (section) => {
    setActiveSection(section);
    sectionsRef[section]?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // Handle admin modal close
  const onAssignAdminClose = () => {
    setAssignAdminModal(false);
    toggleModal();
    fetchLeads();
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]:
        type === "checkbox"
          ? checked
          : value === "true"
          ? true
          : value === "false"
          ? false
          : value,
    }));
  };

  // Handle lead type change with core service mapping
  const handleLeadTypeChange = (e) => {
    const service = e.target.value;
    let coreservice;

    switch (service) {
      case "abroad_counseling":
        coreservice = "Abroad Counseling";
        break;
      case "career_counseling":
      case "subject_selection":
        coreservice = "Career Counseling";
        break;
      case "visa":
      case "others":
        coreservice = "Other Services";
        break;
      case "test_prep":
        coreservice = "Test Preparation";
        break;
      case "profile_building":
        coreservice = "Profile Building";
        break;
      default:
        coreservice = null;
    }

    const selectedService = coreServices.find(
      (item) => item.core_service_name === coreservice
    );

    if (selectedService) {
      setFormData((prevData) => ({
        ...prevData,
        lead_type: service,
        coreservice_id: selectedService.id,
      }));
    } else {
      console.warn("No matching core service found for:", coreservice);
    }
  };

  // Handle multi-select changes
  const handleMultiSelectChange = (selectedOptions, fieldName) => {
    const values = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: values,
    }));
  };

  // Handle radio button changes
  const handleRadioChange = (event) => {
    const { id } = event.target;

    setFormData((prevData) => ({
      ...prevData,
      reference_source: id,
    }));
  };

  // Handle phone input changes
  const handlePhoneChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      phone: value,
    }));
  };

  // Handle alternate phone input changes
  const handleAlternatePhoneChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      alternate_phone: value,
    }));
  };

  // Determine which sections to show based on lead type
  const getVisibleSections = () => {
    if (!formData.lead_type || formData.lead_type === "") {
      return ["leadInfo", "referralSource", "educationCareer"];
    }

    switch (formData.lead_type) {
      case "abroad_counseling":
        return ["leadInfo", "referralSource", "educationCareer"];
      case "career_counseling":
      case "subject_selection":
        return [
          "leadInfo",
          "referralSource",
          "attributesHobbies",
          "careerIntrests",
          "educationCareer",
          "technologyInnovation",
          "selfDescription",
        ];
      case "others":
        return [
          "leadInfo",
          "referralSource",
          "educationCareer",
          "selfDescription",
        ];
      case "test_prep":
        return ["leadInfo", "referralSource", "examTestPrep"];
      case "visa":
        return ["leadInfo", "referralSource", "visa"];
      case "profile_building":
        return ["leadInfo", "referralSource", "educationCareer"];
      default:
        return [];
    }
  };

  const visibleSections = getVisibleSections();

  // Handle country selection in checkbox groups
  const handleChangeCountry = (e) => {
    const { value } = e.target;
    const fieldName = e.target.getAttribute("name");

    setFormData((prevState) => {
      let selectedCountries = prevState[fieldName]
        ? prevState[fieldName].split(", ")
        : [];

      if (selectedCountries.includes(value)) {
        selectedCountries = selectedCountries.filter(
          (country) => country !== value
        );
      } else {
        selectedCountries.push(value);
      }

      return {
        ...prevState,
        [fieldName]:
          selectedCountries.length > 0 ? selectedCountries.join(", ") : "",
      };
    });
  };

  // Validate form before submission
  const validateForm = () => {
    let newErrors = {};

    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Name is required";
    }

    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else {
      // Check if phone exists - exclude child leads and current lead being edited
      if (!parentLead && mode !== "edit") {
        const phoneExists = allLeads.some(
          (lead) => lead.parent_info.phone === formData.phone
        );

        if (phoneExists) {
          newErrors.phone = "A lead already exists with this phone number";
        }
      }
    }

    if (!formData.call_handled_by) {
      newErrors.call_handled_by = "Call handled by is required";
    }

    if (!formData.assign_by) {
      newErrors.assign_by = "Assigned to is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Display error toast for all errors
      Object.values(errors).forEach((error) => {
        toast.error(error);
      });
      return;
    }

    const [firstName, ...rest] = (formData.name || "").trim().split(" ");
    const lastName = rest.length > 0 ? rest.join(" ") : null;

    const formattedData = {
      ...formData,
      first_name: firstName || null,
      last_name: lastName,
      name: formData.name?.trim(),
      subjects_liked:
        typeof formData.subjects_liked === "string"
          ? formData.subjects_liked
          : "",
      subjects_disliked:
        typeof formData.subjects_disliked === "string"
          ? formData.subjects_disliked
          : "",
    };

    try {
      setIsLoading(true);

      if (mode === "edit") {
        await updateLeadForm(selectedLead.id, formattedData);
        toast.success("Lead updated successfully");
      } else {
        if (parentLead) {
          const updatedForm = {
            ...formData,
            parent_id: parentLead?.parent_id.toString(),
            subjects_liked:
              typeof formData.subjects_liked === "string"
                ? formData.subjects_liked
                : "",
            subjects_disliked:
              typeof formData.subjects_disliked === "string"
                ? formData.subjects_disliked
                : "",
          };
          await createLead(updatedForm);
          toast.success("Lead created successfully!");
        } else {
          await createLead(formData);
          toast.success("Lead created successfully!");
        }
      }

      fetchLeads();
      toggleModal();
    } catch (error) {
      console.error(
        "Error creating lead:",
        error.response?.data || error.message
      );
      toast.error("Failed to create lead. Check your input or try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle editing a lead
  const handleEditLead = async (id) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/lead/api/${id}/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const selectedlead = response.data.data.parent_lead || {};
      const parent_info = response.data.data.parent_info || {};
      const childLeads = response.data.data.child_leads || [];

      dispatch(setSelectedLead({ ...parent_info, ...selectedlead }));
      dispatch(setParentLead(parent_info));
      dispatch(setChildLeads(childLeads));
      dispatch(setMode("edit"));
    } catch (err) {
      console.error("Error fetching lead:", err);
      toast.error("Failed to load lead details");
    } finally {
      setIsLoading(false);
    }
  };

  // View a lead's details
  const handleViewLeadForm = async (id) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/lead/api/${id}/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const selectedlead = response.data.data.parent_lead || {};
      const parent_info = response.data.data.parent_info || {};
      const childLeads = response.data.data.child_leads || [];

      dispatch(setSelectedLead({ ...parent_info, ...selectedlead }));
      dispatch(setParentLead(parent_info));
      dispatch(setChildLeads(childLeads));
    } catch (err) {
      console.error("Error fetching lead:", err);
      toast.error("Failed to load lead details");
    } finally {
      setIsLoading(false);
    }
  };

  // Convert a lead to a customer
  const handleConvertToCustomer = async (id) => {
    if (!formData.email || formData.email.trim() === "") {
      toast.error("Email is required to convert to customer.");
      return;
    }

    // Optional: You can also validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    const confirmAction = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to convert this lead to a customer?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Convert!",
    });

    if (!confirmAction.isConfirmed) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/lead/api/${id}/update-status-customer/`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.reminder) {
        Swal.fire({
          title: "Success!",
          text: response.data.reminder,
          icon: "success",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "OK",
        });
      } else {
        Swal.fire({
          title: "Success!",
          text: "Lead converted successfully!",
          icon: "success",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "OK",
        });
      }

      if (selectedLead?.lead_type === "abroad_counseling") {
        setCustomerId(response.data.customer_id);
      } else {
        toggleModal();
        fetchLeads();
      }
    } catch (err) {
      console.error("Error converting lead:", err);
      Swal.fire({
        title: "Error!",
        text: "Failed to convert lead. Please try again.",
        icon: "error",
        confirmButtonColor: "#d33",
        confirmButtonText: "OK",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Send SWOT test
  const sendSwot = async (id) => {
    try {
      setIsLoading(true);
      const res = await sendSwotTest(id);
      toast.success("SWOT test successfully sent");
    } catch (err) {
      console.error("Error sending SWOT test:", err);
      toast.error("Failed to send SWOT test");
    } finally {
      setIsLoading(false);
    }
  };

  // Load core services on component mount
  useEffect(() => {
    getAllCoreService();
  }, []);

  // Set form data when selected lead changes
  useEffect(() => {
    if (selectedLead) {
      const { lead_type_info, ...rest } = selectedLead;

      let intakeMonth = "";
      let intakeYear = "";

      if (lead_type_info?.intake) {
        const intakeDate = new Date(lead_type_info.intake);
        intakeMonth = intakeDate.toLocaleString("default", { month: "long" });
        intakeYear = intakeDate.getFullYear().toString();
      }

      setFormData({
        ...rest,
        ...(lead_type_info || {}),
        subjects_liked: lead_type_info?.subjects_liked
          ? lead_type_info.subjects_liked.split(", ")
          : [],
        subjects_disliked: lead_type_info?.subjects_disliked
          ? lead_type_info.subjects_disliked.split(", ")
          : [],
        name_test_preparation_required: Array.isArray(
          lead_type_info?.name_test_preparation_required
        )
          ? lead_type_info.name_test_preparation_required
          : [],
        intakeMonth,
        intakeYear,
      });
    } else {
      setFormData((prevData) => ({
        ...prevData,
        status: "fresh",
        coreservice_id: 1,
        name: parentLead?.parent_info?.name || "",
        email: parentLead?.parent_info?.email || "",
        phone: parentLead?.parent_info?.phone || "",
        alternate_phone: parentLead?.alternate_phone || "",
        residence_city_state: parentLead?.residence_city_state,
        lead_type: prevData.lead_type || "abroad_counseling",
        // call_handled_by:
        //   prevData.call_handled_by ||
        //   (admins.length > 0 ? admins[0].admin_id : ""),
        // assign_by:
        //   prevData.assign_by || (admins.length > 0 ? admins[0].admin_id : ""),
        call_handled_by:
          prevData.call_handled_by || userDetails?.admin_id || "",
        assign_by: prevData.assign_by || userDetails?.admin_id || "",
        branch: prevData.branch || "Vidhyavihar",
      }));
    }
  }, [selectedLead]);

  // Show assign admin modal when customer ID is available
  useEffect(() => {
    if (customerId) {
      setAssignAdminModal(true);
    }
  }, [customerId]);

  // Render loading spinner when loading
  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-green-500"></div>
          <p className="mt-4 text-green-600 font-semibold">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    // Remove min-h-screen to prevent double scrollbars
    <div className="bg-white rounded-lg shadow-lg flex flex-col h-full overflow-hidden">
      {/* Header */}
      <header className="bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-6 rounded-t-lg shadow-md sticky top-0 z-[1000]">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div className="flex flex-col mb-4 md:mb-0">
            <h1 className="text-xl font-bold">
              {mode === "create-child"
                ? "Create Child Lead"
                : selectedLead
                ? `${selectedLead.name || ""} - ${
                    selectedLead.lead_type === "others"
                      ? selectedLead.session_type
                        ? `Others (${selectedLead.session_type})`
                        : "Others"
                      : selectedLead.lead_type === "subject_selection"
                      ? "Subject Selection"
                      : selectedLead.lead_type
                      ? selectedLead.lead_type.replace(/_/g, " ")
                      : ""
                  }`
                : parentLead
                ? "Create Child Lead"
                : "Create a New Lead"}
            </h1>

            {mode !== "create-child" &&
              mode !== "new" &&
              childLeads &&
              childLeads.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium bg-green-700 px-2 py-1 rounded-md">
                    Child Leads:
                  </span>
                  {childLeads.map((child, index) => (
                    <button
                      key={child.id || index}
                      className="text-sm bg-white text-green-700 hover:bg-green-100 px-2 py-1 rounded-md transition-colors duration-200"
                      onClick={() => handleViewLeadForm(child.id)}
                    >
                      {child.lead_type === "others"
                        ? child.session_type
                          ? `Others (${child.session_type})`
                          : "Others"
                        : child.lead_type === "subject_selection"
                        ? "Subject Selection"
                        : child.lead_type
                        ? child.lead_type.replace(/_/g, " ")
                        : ""}
                    </button>
                  ))}
                </div>
              )}
          </div>

          {mode === "view" && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-white text-green-600 font-medium px-3 py-2 rounded-md hover:bg-green-50 transition-colors duration-200 flex items-center gap-1 shadow-sm"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                Copy Link
              </button>
              {formData.lead_type === "abroad_counseling" && (
                <button
                  onClick={() => setIsCopyEnrollForm(true)}
                  className="bg-white text-green-600 font-medium px-3 py-2 rounded-md hover:bg-green-50 transition-colors duration-200 flex items-center gap-1 shadow-sm"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  Copy Enroll Link
                </button>
              )}
              {selectedLead?.status !== "customer" && (
                <button
                  onClick={() => handleConvertToCustomer(selectedLead.id)}
                  className="bg-white text-green-600 font-medium px-3 py-2 rounded-md hover:bg-green-50 transition-colors duration-200 flex items-center gap-1 shadow-sm"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  Convert to Customer
                </button>
              )}

              {(formData.lead_type === "career_counseling" || formData.lead_type === "subject_selection") && (
                <button
                  className="bg-orange-500 text-white font-medium px-3 py-2 rounded-md hover:bg-orange-600 transition-colors duration-200 flex items-center gap-1 shadow-sm"
                  onClick={() => sendSwot(selectedLead.id)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    />
                  </svg>
                  Send SWOT Test
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main Content - Use flex-grow and proper overflow handling */}
      <div className="flex flex-grow overflow-hidden ">
        {/* Sidebar Navigation - Fixed width, scrollable if needed */}
        <aside className="bg-gray-50 w-64 border-r border-gray-200 shadow-inner overflow-y-auto hidden md:block flex-shrink-0">
          <nav className="p-4 sticky top-0">
            <ul className="space-y-2">
              {visibleSections.includes("leadInfo") && (
                <li>
                  <button
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center font-medium transition-colors duration-200
                      ${
                        activeSection === "leadInfo"
                          ? "bg-green-500 text-white shadow-md"
                          : "bg-white text-gray-700 hover:bg-green-50 hover:text-green-600"
                      }`}
                    onClick={() => handleScroll("leadInfo")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Lead Information
                  </button>
                </li>
              )}

              {visibleSections.includes("referralSource") && (
                <li>
                  <button
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center font-medium transition-colors duration-200
                      ${
                        activeSection === "referralSource"
                          ? "bg-green-500 text-white shadow-md"
                          : "bg-white text-gray-700 hover:bg-green-50 hover:text-green-600"
                      }`}
                    onClick={() => handleScroll("referralSource")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Referral & Source
                  </button>
                </li>
              )}

              {visibleSections.includes("educationCareer") && (
                <li>
                  <button
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center font-medium transition-colors duration-200
                      ${
                        activeSection === "educationCareer"
                          ? "bg-green-500 text-white shadow-md"
                          : "bg-white text-gray-700 hover:bg-green-50 hover:text-green-600"
                      }`}
                    onClick={() => handleScroll("educationCareer")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M12 14l9-5-9-5-9 5 9 5z" />
                      <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
                      />
                    </svg>
                    Education & Career
                  </button>
                </li>
              )}

              {visibleSections.includes("attributesHobbies") && (
                <li>
                  <button
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center font-medium transition-colors duration-200
                      ${
                        activeSection === "attributesHobbies"
                          ? "bg-green-500 text-white shadow-md"
                          : "bg-white text-gray-700 hover:bg-green-50 hover:text-green-600"
                      }`}
                    onClick={() => handleScroll("attributesHobbies")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Attributes & Hobbies
                  </button>
                </li>
              )}

              {visibleSections.includes("careerIntrests") && (
                <li>
                  <button
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center font-medium transition-colors duration-200
                      ${
                        activeSection === "careerIntrests"
                          ? "bg-green-500 text-white shadow-md"
                          : "bg-white text-gray-700 hover:bg-green-50 hover:text-green-600"
                      }`}
                    onClick={() => handleScroll("careerIntrests")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    Career Interests
                  </button>
                </li>
              )}

              {visibleSections.includes("technologyInnovation") && (
                <li>
                  <button
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center font-medium transition-colors duration-200
                      ${
                        activeSection === "technologyInnovation"
                          ? "bg-green-500 text-white shadow-md"
                          : "bg-white text-gray-700 hover:bg-green-50 hover:text-green-600"
                      }`}
                    onClick={() => handleScroll("technologyInnovation")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                    Technology & Innovation
                  </button>
                </li>
              )}

              {visibleSections.includes("examTestPrep") && (
                <li>
                  <button
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center font-medium transition-colors duration-200
                      ${
                        activeSection === "examTestPrep"
                          ? "bg-green-500 text-white shadow-md"
                          : "bg-white text-gray-700 hover:bg-green-50 hover:text-green-600"
                      }`}
                    onClick={() => handleScroll("examTestPrep")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                      />
                    </svg>
                    Exams & Test Prep
                  </button>
                </li>
              )}

              {visibleSections.includes("visa") && (
                <li>
                  <button
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center font-medium transition-colors duration-200
                      ${
                        activeSection === "visa"
                          ? "bg-green-500 text-white shadow-md"
                          : "bg-white text-gray-700 hover:bg-green-50 hover:text-green-600"
                      }`}
                    onClick={() => handleScroll("visa")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                    Visa Details
                  </button>
                </li>
              )}

              {visibleSections.includes("selfDescription") && (
                <li>
                  <button
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center font-medium transition-colors duration-200
                      ${
                        activeSection === "selfDescription"
                          ? "bg-green-500 text-white shadow-md"
                          : "bg-white text-gray-700 hover:bg-green-50 hover:text-green-600"
                      }`}
                    onClick={() => handleScroll("selfDescription")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Self-Description
                  </button>
                </li>
              )}
            </ul>
          </nav>
        </aside>

        {/* Mobile Section Navigation - Only visible on mobile */}
        <div className="md:hidden fixed bottom-16 left-0 right-0 bg-white border-t border-gray-200 shadow-md z-20 p-2">
          <select
            className="w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500"
            value={activeSection}
            onChange={(e) => handleScroll(e.target.value)}
          >
            {visibleSections.includes("leadInfo") && (
              <option value="leadInfo">Lead Information</option>
            )}
            {visibleSections.includes("referralSource") && (
              <option value="referralSource">Referral & Source</option>
            )}
            {visibleSections.includes("educationCareer") && (
              <option value="educationCareer">Education & Career</option>
            )}
            {visibleSections.includes("attributesHobbies") && (
              <option value="attributesHobbies">Attributes & Hobbies</option>
            )}
            {visibleSections.includes("careerIntrests") && (
              <option value="careerIntrests">Career Interests</option>
            )}
            {visibleSections.includes("technologyInnovation") && (
              <option value="technologyInnovation">
                Technology & Innovation
              </option>
            )}
            {visibleSections.includes("examTestPrep") && (
              <option value="examTestPrep">Exams & Test Prep</option>
            )}
            {visibleSections.includes("visa") && (
              <option value="visa">Visa Details</option>
            )}
            {visibleSections.includes("selfDescription") && (
              <option value="selfDescription">Self-Description</option>
            )}
          </select>
        </div>

        {/* Form Content */}
        <main className="flex-grow flex flex-col overflow-hidden ">
          {/* This div is scrollable and contains all form content */}
          <div className="flex-grow overflow-auto relative">
            <div className="p-6 md:p-8">
              {/* Lead Information Section */}
              <section
                ref={sectionsRef.leadInfo}
                id="lead-info"
                className="mb-8 bg-white rounded-lg shadow-md overflow-hidden"
              >
                <div className="bg-green-50 px-6 py-4 border-b border-green-100">
                  <h2 className="text-lg font-semibold text-green-800 flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Lead Information
                  </h2>
                </div>

                <div className="p-6">
                  <div className="mb-6">
                    <h3 className="text-md font-semibold mb-4 pb-2 border-b border-gray-200 text-gray-800">
                      1. Lead Name & Contact Info
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label
                          className="block text-sm font-medium mb-1 text-gray-700"
                          htmlFor="name"
                        >
                          Name <span className="text-red-500">*</span>
                        </label>
                        {mode === "view" ? (
                          <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                            {formData.name || "N/A"}
                          </p>
                        ) : (
                          <div>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              className={`w-full border ${
                                errors.name
                                  ? "border-red-500"
                                  : "border-gray-300"
                              } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500`}
                              onChange={handleChange}
                              value={formData.name || ""}
                              required
                              placeholder="John Doe"
                            />
                            {errors.name && (
                              <p className="mt-1 text-sm text-red-600">
                                {errors.name}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <label
                          className="block text-sm font-medium mb-1 text-gray-700"
                          htmlFor="phone"
                        >
                          Phone <span className="text-red-500">*</span>
                        </label>
                        {mode === "view" ? (
                          <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                            {formData.phone || "N/A"}
                          </p>
                        ) : (
                          <div>
                            <PhoneInput
                              country={"in"}
                              value={formData.phone}
                              onChange={handlePhoneChange}
                              inputClass={`${
                                errors.phone
                                  ? "border-red-500"
                                  : "border-gray-300"
                              }`}
                              inputStyle={{
                                width: "100%",
                                height: "40px",
                                borderRadius: "6px",
                                borderColor: errors.phone
                                  ? "#f56565"
                                  : "#e2e8f0",
                                paddingLeft: "50px",
                              }}
                              containerStyle={{
                                marginTop: "0",
                              }}
                              dropdownStyle={{ width: "320px" }}
                              required
                            />
                            {errors.phone && (
                              <p className="mt-1 text-sm text-red-600">
                                {errors.phone}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <label
                          className="block text-sm font-medium mb-1 text-gray-700"
                          htmlFor="alternate_phone"
                        >
                          Alternate Phone
                        </label>
                        {mode === "view" ? (
                          <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                            {formData.alternate_phone || "N/A"}
                          </p>
                        ) : (
                          <PhoneInput
                            country={"in"}
                            value={formData.alternate_phone}
                            onChange={handleAlternatePhoneChange}
                            inputStyle={{
                              width: "100%",
                              height: "40px",
                              borderRadius: "6px",
                              borderColor: "#e2e8f0",
                              paddingLeft: "50px",
                            }}
                            containerStyle={{
                              marginTop: "0",
                            }}
                            dropdownStyle={{ width: "320px" }}
                          />
                        )}
                      </div>

                      <div>
                        <label
                          className="block text-sm font-medium mb-1 text-gray-700"
                          htmlFor="email"
                        >
                          Email ID
                        </label>
                        {mode === "view" ? (
                          <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                            {formData.email || "N/A"}
                          </p>
                        ) : (
                          <input
                            type="email"
                            id="email"
                            name="email"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                            onChange={handleChange}
                            value={formData.email || ""}
                            placeholder="john.doe@example.com"
                          />
                        )}
                      </div>

                      <div className="md:col-span-2">
                        <label
                          className="block text-sm font-medium mb-1 text-gray-700"
                          htmlFor="residence_city_state"
                        >
                          Address
                        </label>
                        {mode === "view" ? (
                          <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                            {formData.residence_city_state || "N/A"}
                          </p>
                        ) : (
                          <input
                            type="text"
                            id="residence_city_state"
                            name="residence_city_state"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                            onChange={handleChange}
                            value={formData.residence_city_state || ""}
                            placeholder="123 Street Name, City, State"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Lead Status & Assignment */}
                  <div>
                    <h3 className="text-md font-semibold mb-4 pb-2 border-b border-gray-200 text-gray-800">
                      2. Lead Status & Assignment
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label
                          className="block text-sm font-medium mb-1 text-gray-700"
                          htmlFor="status"
                        >
                          Status
                        </label>
                        {mode === "view" ? (
                          <div className="flex items-center">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium 
                              ${
                                formData.status === "fresh"
                                  ? "bg-green-100 text-green-800"
                                  : formData.status === "enquiry"
                                  ? "bg-blue-100 text-blue-800"
                                  : formData.status === "lost"
                                  ? "bg-red-100 text-red-800"
                                  : formData.status === "junk"
                                  ? "bg-gray-100 text-gray-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {formData.status || "N/A"}
                            </span>
                          </div>
                        ) : (
                          <select
                            id="status"
                            name="status"
                            value={formData.status || "fresh"}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                          >
                            <option value="fresh">Fresh</option>
                            <option value="enquiry">Enquiry</option>
                            <option value="lost">Lost</option>
                            <option value="junk">Junk</option>
                          </select>
                        )}
                      </div>
                      <div>
                        <label
                          className="block text-sm font-medium mb-1 text-gray-700"
                          htmlFor="type"
                        >
                          Lead Type
                        </label>
                        {mode === "view" || mode === "edit" ? (
                          <div className="flex items-center">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {(formData.lead_type || "")
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </span>
                          </div>
                        ) : (
                          <select
                            id="type"
                            name="lead_type"
                            value={formData.lead_type || ""}
                            onChange={(e) => handleLeadTypeChange(e)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                          >
                            <option value="abroad_counseling">
                              Abroad Counseling
                            </option>
                            <option value="career_counseling">
                              Career Counseling
                            </option>
                            <option value="subject_selection">
                              Subject Selection
                            </option>
                            <option value="test_prep">Test Prep</option>
                            <option value="visa">Visa</option>
                            <option value="profile_building">
                              Profile Building
                            </option>
                            <option value="others">
                              Others
                            </option>
                          </select>
                        )}
                      </div>

                      <div>
                        <label
                          className="block text-sm font-medium mb-1 text-gray-700"
                          htmlFor="call_handled_by"
                        >
                          Call Handled By
                        </label>
                        {mode === "view" ? (
                          <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                            {admins.filter(
                              (admin) =>
                                admin.admin_id === formData.call_handled_by
                            )[0]?.username || "N/A"}
                          </p>
                        ) : (
                          <>
                            <select
                              id="call_handled_by"
                              name="call_handled_by"
                              value={formData.call_handled_by || ""}
                              onChange={handleChange}
                              className={`w-full border ${
                                errors.call_handled_by
                                  ? "border-red-500"
                                  : "border-gray-300"
                              } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white`}
                            >
                              <option value="">Select Admin</option>
                              {admins
                                .filter((admin) => admin.role === "admin")
                                .map((admin) => (
                                  <option
                                    key={admin.admin_id}
                                    value={admin.admin_id}
                                  >
                                    {admin.username}
                                  </option>
                                ))}
                            </select>
                            {errors.call_handled_by && (
                              <p className="mt-1 text-sm text-red-600">
                                {errors.call_handled_by}
                              </p>
                            )}
                          </>
                        )}
                      </div>

                      <div>
                        <label
                          className="block text-sm font-medium mb-1 text-gray-700"
                          htmlFor="assign_by"
                        >
                          Assigned To
                        </label>
                        {mode === "view" ? (
                          <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                            {admins.filter(
                              (admin) => admin.admin_id === formData.assign_by
                            )[0]?.username || "N/A"}
                          </p>
                        ) : (
                          <>
                            <select
                              id="assign_by"
                              name="assign_by"
                              value={formData.assign_by || ""}
                              onChange={handleChange}
                              className={`w-full border ${
                                errors.assign_by
                                  ? "border-red-500"
                                  : "border-gray-300"
                              } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white`}
                            >
                              <option value="">Select Admin</option>
                              {admins
                                .filter((admin) => admin.role === "admin")
                                .map((admin) => (
                                  <option
                                    key={admin.admin_id}
                                    value={admin.admin_id}
                                  >
                                    {admin.username}
                                  </option>
                                ))}
                            </select>
                            {errors.assign_by && (
                              <p className="mt-1 text-sm text-red-600">
                                {errors.assign_by}
                              </p>
                            )}
                          </>
                        )}
                      </div>

                      <div>
                        <label
                          className="block text-sm font-medium mb-1 text-gray-700"
                          htmlFor="branch"
                        >
                          Branch
                        </label>
                        {mode === "view" ? (
                          <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                            {formData.branch || "N/A"}
                          </p>
                        ) : (
                          <select
                            id="branch"
                            name="branch"
                            value={formData.branch || ""}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                          >
                            <option value="">Select a Branch</option>
                            <option value="Andheri">Andheri</option>
                            <option value="Vidhyavihar">Vidhyavihar</option>
                          </select>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="contacted_today"
                          className="block text-sm font-medium mb-1 text-gray-700"
                        >
                          Contacted Today
                        </label>
                        {mode === "view" ? (
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              formData.contacted_today
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {formData.contacted_today ? "Yes" : "No"}
                          </span>
                        ) : (
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              id="contacted_today"
                              name="contacted_today"
                              checked={formData.contacted_today || false}
                              onChange={handleChange}
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-gray-300 rounded-full peer peer-checked:bg-green-400 transition-colors duration-200"></div>
                            <div className="absolute left-1 top-1 w-3 h-3 bg-white rounded-full peer-checked:translate-x-4 transition-transform duration-200"></div>
                          </label>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700">
                          Date Contacted
                        </label>
                        {mode === "view" ? (
                          <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                            {formatDateTime(formData.contacted_at) || "N/A"}
                          </p>
                        ) : (
                          <input
                            type="date"
                            name="contacted_at"
                            value={
                              formData.contacted_at
                                ? new Date(formData.contacted_at)
                                    .toISOString()
                                    .split("T")[0]
                                : ""
                            }
                            onChange={handleChange}
                            className="border border-gray-300 rounded-md py-1 px-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Referral & Source Details Section */}
              <section
                ref={sectionsRef.referralSource}
                id="referral-source"
                className="mb-8 bg-white rounded-lg shadow-md overflow-hidden"
              >
                <div className="bg-green-50 px-6 py-4 border-b border-green-100">
                  <h2 className="text-lg font-semibold text-green-800 flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Referral & Source Details
                  </h2>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Reference Source Options */}
                    <div>
                      <h3 className="text-md font-semibold mb-4 pb-2 border-b border-gray-200 text-gray-800">
                        1. How did you get our reference?
                      </h3>

                      {mode === "view" ? (
                        <p className="bg-gray-50 p-3 rounded-md border border-gray-200">
                          {formData.reference_source || "N/A"}
                        </p>
                      ) : (
                        <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                          {[
                            "Reference of Friend/Relative",
                            "Word of Mouth",
                            "Google",
                            "Walk in",
                            "Seminar",
                            "Others",
                          ].map((source) => (
                            <div key={source} className="flex items-center">
                              <input
                                type="radio"
                                id={source}
                                name="reference_source"
                                checked={formData.reference_source === source}
                                onChange={handleRadioChange}
                                disabled={mode === "view"}
                                className="w-5 h-5 text-green-600 border-gray-300 focus:ring-green-500"
                              />
                              <label
                                htmlFor={source}
                                className="ml-3 text-sm text-gray-700"
                              >
                                {source}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Reference Details */}
                    {formData.reference_source &&
                      formData.reference_source ===
                        "Reference of Friend/Relative" && (
                        <div>
                          <h3 className="text-md font-semibold mb-4 pb-2 border-b border-gray-200 text-gray-800">
                            2. Reference Details
                          </h3>

                          <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700">
                              Reference name/Reference ID{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            {mode === "view" ? (
                              <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                                {formData.reference_name_id || "N/A"}
                              </p>
                            ) : (
                              <input
                                type="text"
                                id="reference_name_id"
                                name="reference_name_id"
                                value={formData.reference_name_id || ""}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                                placeholder="Enter reference name or ID"
                              />
                            )}
                          </div>
                        </div>
                      )}

                    {formData.reference_source === "Others" && (
                      <div>
                        <h3 className="text-md font-semibold mb-4 pb-2 border-b border-gray-200 text-gray-800">
                          2. Other Reference Source
                        </h3>

                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-700">
                            Please specify the source{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          {mode === "view" ? (
                            <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                              {formData.other_reference_source || "N/A"}
                            </p>
                          ) : (
                            <input
                              type="text"
                              id="other_reference_source"
                              name="other_reference_source"
                              value={formData.other_reference_source || ""}
                              onChange={handleChange}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                              placeholder="Please specify source"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* Render the appropriate lead type form */}
              {(!formData.lead_type || formData.lead_type === "") && (
                <CreateLeadAbroadForm
                  mode={mode}
                  formData={formData}
                  setFormData={setFormData}
                  handleChange={handleChange}
                  sectionsRef={sectionsRef}
                />
              )}

              {formData.lead_type === "abroad_counseling" && (
                <CreateLeadAbroadForm
                  mode={mode}
                  formData={formData}
                  setFormData={setFormData}
                  handleChange={handleChange}
                  sectionsRef={sectionsRef}
                />
              )}

              {(formData.lead_type === "career_counseling" || formData.lead_type === "subject_selection") && (
                <CreateLeadCareerCounsellingForm
                  mode={mode}
                  formData={formData}
                  setFormData={setFormData}
                  handleChange={handleChange}
                  transformTagsToOptions={transformTagsToOptions}
                  handleMultiSelectChange={handleMultiSelectChange}
                  sectionsRef={sectionsRef}
                />
              )}

              {formData.lead_type === "others" && (
                <CreateLeadOthersForm
                  mode={mode}
                  formData={formData}
                  setFormData={setFormData}
                  handleChange={handleChange}
                  sectionsRef={sectionsRef}
                />
              )}

              {formData.lead_type === "test_prep" && (
                <CreateLeadTestPrepForm
                  mode={mode}
                  formData={formData}
                  setFormData={setFormData}
                  handleChange={handleChange}
                  transformTagsToOptions={transformTagsToOptions}
                  handleMultiSelectChange={handleMultiSelectChange}
                  handleChangeCountry={handleChangeCountry}
                  sectionsRef={sectionsRef}
                />
              )}

              {formData.lead_type === "visa" && (
                <CreateLeadVisaForm
                  mode={mode}
                  formData={formData}
                  setFormData={setFormData}
                  handleChange={handleChange}
                  transformTagsToOptions={transformTagsToOptions}
                  handleMultiSelectChange={handleMultiSelectChange}
                  handleChangeCountry={handleChangeCountry}
                  sectionsRef={sectionsRef}
                />
              )}

              {formData.lead_type === "profile_building" && (
                <CreateLeadProfileBuildingForm
                  mode={mode}
                  formData={formData}
                  setFormData={setFormData}
                  handleChange={handleChange}
                  transformTagsToOptions={transformTagsToOptions}
                  handleMultiSelectChange={handleMultiSelectChange}
                  handleChangeCountry={handleChangeCountry}
                  sectionsRef={sectionsRef}
                />
              )}
            </div>
            {/* Form Controls - Fixed at Bottom */}
            <div className="bg-white border-t border-gray-200 p-4 shadow-md flex justify-center items-center sticky bottom-0 h-auto w-full z-10">
              <div className="flex justify-center space-x-4">
                {mode !== "view" && (
                  <button
                    onClick={toggleModal}
                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-sm transition-colors duration-200 flex items-center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Cancel
                  </button>
                )}

                <button
                  onClick={(e) => {
                    if (mode === "view") {
                      if (selectedLead?.id) {
                        handleEditLead(selectedLead.id);
                      } else {
                        console.warn("No lead selected for editing.");
                      }
                    } else {
                      handleSubmit(e);
                    }
                  }}
                  className={`px-6 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-md transition-colors duration-200 flex items-center ${
                    mode === "view"
                      ? "bg-green-600 hover:bg-green-700 text-white"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {mode === "view" ? (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit Lead
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {mode === "edit" ? "Update Lead" : "Save Lead"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Modals */}
      {assignAdminModal && customerId && (
        <AssignToAdminModal
          admins={admins}
          onClose={onAssignAdminClose}
          customerId={customerId}
          toggleModal={toggleModal}
          fetchLeads={fetchLeads}
        />
      )}

      <CopyLinkModal
        type="lead"
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        link={link}
      />
      {/* {formData.lead_type === "abroad_counseling" && ( */}
      <CopyLinkModal
        type="enroll"
        isOpen={isCopyEnrollForm}
        onClose={() => setIsCopyEnrollForm(false)}
        link={enrolllink}
      />
      {/* )} */}

      {/* Custom CSS for toggle switch */}
      <style jsx>{`
        .toggle-checkbox:checked {
          right: 0;
          border-color: #68d391;
        }
        .toggle-checkbox:checked + .toggle-label {
          background-color: #68d391;
        }
        .toggle-checkbox {
          right: 0;
          z-index: 1;
          border-color: #cbd5e0;
          top: -0.15rem;
        }
        .toggle-label {
          display: block;
          overflow: hidden;
          cursor: pointer;
          border-radius: 9999px;
          transition: background-color 0.2s ease-in;
        }
      `}</style>
    </div>
  );
}

export default CreateLeadForm;
